import { ModalityType } from '@littlebird/sdk';
import { opensTurn } from '@littlebird/ui-utils';

import { ReasoningStatus } from '@/components/blocks/Message/reasoning';

import { buildTurnActivityEntries } from './turnActivityEntries';

import type {
  ChatItem,
  TurnActivityChildItem,
  TurnActivityItem,
} from './chatItems.types';

const isActivityItem = (item: ChatItem): item is TurnActivityChildItem =>
  item.type === 'reasoning' ||
  (item.type === 'assistantResponse' && !item.message.fileIds?.length);

// Every user message ends a run of activity rows, a joined one too, so the answer before it stays visible
const endsActivityRun = (item: ChatItem): boolean =>
  item.type === 'userMessage' || item.type === 'dateSeparator';

const isActivityDisplayItem = (item: ChatItem): boolean =>
  item.type === 'reasoning' ||
  item.type === 'turnActivity' ||
  item.type === 'thinking';

// Counts only messages that open a turn, so activity after a message joined into the first turn (`joinedTurn`) stays hidden
export const omitFirstTurnActivity = (items: ChatItem[]): ChatItem[] => {
  let turnCount = 0;
  return items.filter((item) => {
    if (item.type === 'userMessage' && opensTurn(item.message)) turnCount += 1;
    return turnCount !== 1 || !isActivityDisplayItem(item);
  });
};

const getActivityItemKey = (item: TurnActivityChildItem): string =>
  item.type === 'assistantResponse' ? item.message.id : item.id;

const findFinalReplyIndex = (turn: ChatItem[]): number => {
  for (let index = turn.length - 1; index >= 0; index -= 1) {
    const item = turn[index];
    if (item.type === 'reasoning') return -1;
    if (isActivityItem(item)) return index;
  }
  return -1;
};

const carriesTodoWrite = (item: ChatItem | undefined): boolean =>
  item?.type === 'assistantResponse' &&
  Boolean(
    item.message.modalitiesUsed?.some(
      (modality) => modality.type === ModalityType.TodoWrite
    )
  );

const findVisibleReplyIndexes = (turn: ChatItem[]): Set<number> => {
  const finalReplyIndex = findFinalReplyIndex(turn);
  if (finalReplyIndex === -1) return new Set();

  const visibleReplyIndexes = new Set([finalReplyIndex]);
  for (let index = finalReplyIndex - 1; index >= 0; index -= 1) {
    if (turn[index].type !== 'assistantResponse') continue;
    if (carriesTodoWrite(turn[index])) visibleReplyIndexes.add(index);
    break;
  }
  return visibleReplyIndexes;
};

const MIN_ENTRIES_TO_COLLAPSE = 2;

const toTurnActivity = (run: TurnActivityChildItem[]): TurnActivityItem => ({
  type: 'turnActivity',
  id: `turn-activity-${getActivityItemKey(run[0])}`,
  items: run,
  entries: buildTurnActivityEntries(run),
  isActive: run.some(
    (item) =>
      item.type === 'reasoning' && item.status === ReasoningStatus.Pending
  ),
});

const collapseTurn = (turn: ChatItem[]): ChatItem[] => {
  const visibleReplyIndexes = findVisibleReplyIndexes(turn);
  const result: ChatItem[] = [];
  let run: TurnActivityChildItem[] = [];

  const flushRun = () => {
    if (run.length === 0) return;
    const row = toTurnActivity(run);
    if (row.entries.length >= MIN_ENTRIES_TO_COLLAPSE) result.push(row);
    else result.push(...run);
    run = [];
  };

  turn.forEach((item, index) => {
    if (isActivityItem(item) && !visibleReplyIndexes.has(index)) {
      run.push(item);
      return;
    }
    flushRun();
    result.push(item);
  });
  flushRun();

  // A turn that ends in a tool call folds every reply, and a folded reply draws no cards
  const lastRow = result.at(-1);
  if (lastRow?.type === 'turnActivity') {
    const lastItem = lastRow.items.at(-1);
    lastRow.cardsFromMessageId =
      lastItem?.type === 'assistantResponse'
        ? lastItem.message.id
        : lastItem?.messageIds.at(-1);
  }

  return result;
};

const withTrailingRowActive = (items: ChatItem[]): ChatItem[] => {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (endsActivityRun(item) || item.type === 'reasoning') break;
    if (item.type !== 'turnActivity') continue;
    const withActiveRow = [...items];
    withActiveRow[index] = { ...item, isActive: true };
    return withActiveRow;
  }
  return items;
};

export const collapseTurnActivity = (
  items: ChatItem[],
  isThreadBusy = false
): ChatItem[] => {
  const result: ChatItem[] = [];
  let turn: ChatItem[] = [];

  const flushTurn = () => {
    if (turn.length === 0) return;
    result.push(...collapseTurn(turn));
    turn = [];
  };

  for (const item of items) {
    if (endsActivityRun(item)) {
      flushTurn();
      result.push(item);
      continue;
    }
    turn.push(item);
  }
  flushTurn();

  return isThreadBusy ? withTrailingRowActive(result) : result;
};
